import React, { useRef, useState, useEffect } from "react";
import { saveAs } from "file-saver";

const BadgeGenerator = () => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const drawBadge = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const frameImage = new Image();
    frameImage.src = "./Frame2.png";

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    frameImage.onload = () => {
      ctx.drawImage(frameImage, 0, 0, canvas.width, canvas.height);

      if (uploadedImage) {
        const userImage = new Image();
        userImage.src = uploadedImage;

        userImage.onload = () => {
          ctx.drawImage(
            userImage,
            imagePosition.x,
            imagePosition.y,
            imageSize.width,
            imageSize.height
          );

          ctx.drawImage(frameImage, 0, 0, canvas.width, canvas.height);
        };
      }
    };
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    canvas.toBlob((blob) => {
      saveAs(blob, "DKS frame gio.png");
    });
  };

  // Utility function to handle both mouse and touch events
  const getPointerPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    let mouseX = 0;
    let mouseY = 0;

    if (e.touches) {
      // For touch event
      mouseX = e.touches[0].clientX - rect.left;
      mouseY = e.touches[0].clientY - rect.top;
    } else {
      // For mouse event
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
    }

    return { mouseX, mouseY };
  };

  const handleMouseDown = (e) => {
    const { mouseX, mouseY } = getPointerPosition(e);

    if (
      mouseX >= imagePosition.x &&
      mouseX <= imagePosition.x + imageSize.width &&
      mouseY >= imagePosition.y &&
      mouseY <= imagePosition.y + imageSize.height
    ) {
      setIsDragging(true);
      setStartPos({ x: mouseX - imagePosition.x, y: mouseY - imagePosition.y });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const { mouseX, mouseY } = getPointerPosition(e);
    setImagePosition({
      x: mouseX - startPos.x,
      y: mouseY - startPos.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const updateFrameSize = () => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const newFrameSize = Math.min(containerWidth, 768); // Max size of 768px
      setImageSize({
        width: newFrameSize,
        height: newFrameSize,
      });

      setImagePosition({
        x: Math.min(imagePosition.x, newFrameSize - imageSize.width),
        y: Math.min(imagePosition.y, newFrameSize - imageSize.height),
      });
    }
  };

  useEffect(() => {
    updateFrameSize();
    window.addEventListener("resize", updateFrameSize);

    return () => {
      window.removeEventListener("resize", updateFrameSize);
    };
  }, []);

  useEffect(() => {
    drawBadge();
  }, [uploadedImage, imagePosition, imageSize]);

  useEffect(() => {
    const canvas = canvasRef.current;

    // Mouse events
    canvas.addEventListener("mousedown", handleMouseDown);
    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("mouseup", handleMouseUp);
    canvas.addEventListener("mouseleave", handleMouseUp);

    // Touch events
    canvas.addEventListener("touchstart", handleMouseDown);
    canvas.addEventListener("touchmove", handleMouseMove);
    canvas.addEventListener("touchend", handleMouseUp);
    canvas.addEventListener("touchcancel", handleMouseUp);

    return () => {
      canvas.removeEventListener("mousedown", handleMouseDown);
      canvas.removeEventListener("mousemove", handleMouseMove);
      canvas.removeEventListener("mouseup", handleMouseUp);
      canvas.removeEventListener("mouseleave", handleMouseUp);

      canvas.removeEventListener("touchstart", handleMouseDown);
      canvas.removeEventListener("touchmove", handleMouseMove);
      canvas.removeEventListener("touchend", handleMouseUp);
      canvas.removeEventListener("touchcancel", handleMouseUp);
    };
  }, [isDragging, imagePosition]);

  return (
    <div
      ref={containerRef}
      className="text-center p-4 sm:p-6 md:p-8 lg:p-12 max-w-3xl mx-auto font-sans"
    >
      <img
        src="./dks_title.png"
        alt="logo"
        className="w-full h-auto max-w-md mx-auto mb-4"
      />
      <div className="mb-6">
        <canvas
          ref={canvasRef}
          width={imageSize.width}
          height={imageSize.height}
          className={`border border-gray-300 bg-black shadow-lg ${
            isDragging ? "cursor-move" : "cursor-pointer"
          } w-full max-w-[550px] mx-auto`}
        ></canvas>
      </div>
      <div className="mb-6">
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="px-4 py-2 text-lg bg-gray-100 border border-gray-300 rounded-lg cursor-pointer w-full max-w-xs mx-auto"
        />
      </div>
      <div>
        <button
          onClick={handleDownload}
          disabled={!uploadedImage}
          className="px-6 py-3 text-lg text-white bg-blue-500 hover:bg-blue-600 rounded-lg focus:outline-none disabled:bg-gray-300 w-full max-w-xs mx-auto"
        >
          Download Badge
        </button>
      </div>
    </div>
  );
};

export default BadgeGenerator;